import { useEffect, useRef, useState } from 'react'

import { ActionIcon, CloseButton, TextInput, Tooltip } from '@mantine/core'

import { useMutation } from '@redwoodjs/web'

import DNButton from 'src/components/Buttons/Button/DNButton'
import { UPDATE_DOCUMENT } from 'src/graphql/documents.graphql'
import IconCheckmark from 'src/icons/IconCheckmark'
import IconChevronLeft from 'src/icons/IconChevronLeft'
import IconDownload from 'src/icons/IconDownload'
import IconEdit from 'src/icons/IconEdit'
import IconLeftArrow from 'src/icons/IconLeftArrow'
import IconPrint from 'src/icons/IconPrint'

import { toast } from '../../Toast/Toast'

// Stepping through a list of documents. `onPrevious` / `onNext` are undefined
// at the ends of the list. The filter fields describe the list being stepped
// through when it is narrower than all of the applicant's documents.
export type DocumentNavigation = {
  position: number
  total: number
  // The list of documents hasn't arrived yet: the controls show but are
  // disabled, and Approve stays on the document instead of leaving it
  loading?: boolean
  onPrevious?: () => void
  onNext?: () => void
  filterLabel?: string
  onClearFilter?: () => void
}

const isTypingTarget = (el: HTMLElement | null) =>
  !!el &&
  (el.tagName === 'INPUT' ||
    el.tagName === 'TEXTAREA' ||
    el.tagName === 'SELECT' ||
    el.isContentEditable)

// Modals, menus and option lists own the arrow keys while they are open
const isInsideOverlay = (el: HTMLElement | null) =>
  !!el?.closest?.(
    '[role="dialog"], [aria-modal="true"], [role="menu"], [role="listbox"]'
  )

// Arrow keys step through documents unless they belong to something else: a
// text field, an open modal/menu, or a browser shortcut (Alt+Left is Back).
const shouldIgnoreArrowKey = (e: KeyboardEvent) => {
  const el = e.target as HTMLElement | null
  return (
    e.defaultPrevented ||
    e.altKey ||
    e.ctrlKey ||
    e.metaKey ||
    e.shiftKey ||
    isTypingTarget(el) ||
    isInsideOverlay(el)
  )
}

const DocumentPreviewHeader = ({
  fileName,
  documentId,
  onRender,
  showBackButton,
  showClosebutton,
  onBack,
  onPrint,
  onDownload,
  onApprove,
  showActions,
  navigation,
}: {
  fileName: string
  documentId: string
  onRender: any[]
  showBackButton: boolean
  showClosebutton: boolean
  onBack: () => void
  onPrint: () => void
  onDownload: () => void
  onApprove: () => void
  showActions: boolean
  navigation?: DocumentNavigation
}) => {
  const [editMode, setEditMode] = useState(false)
  const [name, setName] = useState(fileName)

  const [updateDocument] = useMutation(UPDATE_DOCUMENT, {
    refetchQueries: onRender,
    onCompleted: () => {
      toast('Document updated successfully', 'success')
      setEditMode(false)
    },
    onError: () => {
      toast('Unable to update document, please try again.', 'error')
    },
  })

  // Focus the rename field as soon as edit is clicked so the first keystroke
  // types (and arrow keys move the caret) instead of stepping documents.
  const nameInputRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    const input = nameInputRef.current
    if (editMode && input) {
      input.focus()
      input.setSelectionRange(input.value.length, input.value.length)
    }
  }, [editMode])

  const onPrevious = navigation?.onPrevious
  const onNext = navigation?.onNext

  // Arrow keys step through the documents (see shouldIgnoreArrowKey for when
  // they don't, e.g. while typing in the rename or date/category fields).
  useEffect(() => {
    if (!navigation) return undefined
    const handleKeyDown = (e: KeyboardEvent) => {
      if (shouldIgnoreArrowKey(e)) return
      if (e.key === 'ArrowLeft') onPrevious?.()
      if (e.key === 'ArrowRight') onNext?.()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [navigation, onPrevious, onNext])

  return (
    <div className="flex min-h-[3.5rem] items-center justify-between">
      <div className="flex w-96 flex-1 flex-row items-center gap-4 overflow-hidden">
        {showBackButton && (
          <ActionIcon variant="subtle" className="h-6 w-6" onClick={onBack}>
            <IconLeftArrow className="fill-doubleNickel-white stroke-doubleNickel-gray-600" />
          </ActionIcon>
        )}
        {editMode ? (
          <TextInput
            classNames={{
              root: 'w-[75%]',
              input: 'text-lg font-bold text-doubleNickel-gray-600 truncate',
            }}
            value={name}
            ref={nameInputRef}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                if (name !== fileName) {
                  updateDocument({
                    variables: {
                      id: documentId,
                      input: { fileName: name },
                    },
                  })
                } else {
                  setEditMode(false)
                }
              }
            }}
            style={{ minWidth: 0 }}
          />
        ) : (
          <Tooltip label={fileName} openDelay={400} withArrow>
            <div className="line-clamp-2 min-w-0 break-words text-lg font-bold leading-7 text-doubleNickel-gray-600">
              {fileName}
            </div>
          </Tooltip>
        )}
        <Tooltip label={editMode ? 'Save' : 'Edit'}>
          <ActionIcon
            variant="subtle"
            color="gray"
            onClick={() => {
              if (editMode) {
                if (name !== fileName) {
                  updateDocument({
                    variables: {
                      id: documentId,
                      input: { fileName: name },
                    },
                  })
                } else {
                  setEditMode(false)
                }
              } else {
                setEditMode(true)
              }
            }}
            size="lg"
          >
            {editMode ? (
              <IconCheckmark className="h-4 fill-none stroke-doubleNickel-gray-500" />
            ) : (
              <IconEdit className="h-4 fill-none stroke-doubleNickel-gray-500" />
            )}
          </ActionIcon>
        </Tooltip>
      </div>
      <div className="flex flex-row items-center gap-4">
        <Tooltip label="Download">
          <ActionIcon
            variant="subtle"
            color="gray"
            onClick={onDownload}
            size="lg"
          >
            <IconDownload className="h-6 fill-none stroke-doubleNickel-gray-500" />
          </ActionIcon>
        </Tooltip>
        <Tooltip label="Print">
          <ActionIcon variant="subtle" color="gray" onClick={onPrint} size="lg">
            <IconPrint className="h-6 fill-none stroke-doubleNickel-gray-500" />
          </ActionIcon>
        </Tooltip>
        {showActions && (
          <DNButton onClick={onApprove} variant="success" className="w-24">
            Approve
          </DNButton>
        )}
        {showClosebutton && (
          <div className="rotate ml-auto">
            <CloseButton onClick={onBack} />
          </div>
        )}
        {/* Last, so it stays put whether or not Approve is shown */}
        {navigation && (
          <div className="flex items-center gap-1">
            {navigation.filterLabel && (
              <span
                title={navigation.filterLabel}
                className="mr-1 flex max-w-[16rem] items-center gap-1 rounded-full bg-doubleNickel-gray-100 py-0.5 pl-2 pr-1 text-xs text-doubleNickel-gray-700"
              >
                <span className="truncate">{navigation.filterLabel}</span>
                <CloseButton
                  size="xs"
                  aria-label="Clear filter"
                  onClick={navigation.onClearFilter}
                />
              </span>
            )}
            <ActionIcon
              variant="subtle"
              color="gray"
              aria-label="Previous document"
              disabled={navigation.loading || !onPrevious}
              onClick={onPrevious}
            >
              <IconChevronLeft className="fill-none stroke-doubleNickel-gray-600" />
            </ActionIcon>
            <span className="min-w-[44px] text-center text-xs font-medium tabular-nums text-doubleNickel-gray-600">
              {navigation.loading
                ? '– / –'
                : `${navigation.position} / ${navigation.total}`}
            </span>
            <ActionIcon
              variant="subtle"
              color="gray"
              aria-label="Next document"
              disabled={navigation.loading || !onNext}
              onClick={onNext}
            >
              <IconChevronLeft className="rotate-180 fill-none stroke-doubleNickel-gray-600" />
            </ActionIcon>
          </div>
        )}
      </div>
    </div>
  )
}

export default DocumentPreviewHeader
